import { forwardRef, Suspense, useRef } from "react"
import { DateTime } from "luxon"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_ENTITIES_ROUTE, SUPPORT_ROUTE } from "../../../../../../constants/routes"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { FormikProps } from "formik"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"

import { fetchEntitiesById, updateSupplierApi } from "../createSupplierApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router"
import { SupplierFormValues, SupplierWithHistory } from "../supplierTypes"
import { SupplierCreateUpdateForm } from "../components/SupplierCreateUpdateForm"
import { AgreementOptions, BusinessType, EntityHistory } from "../../supportEntitiesTypes"
import { getAddress, getContactDetails, getInvoiceDetails } from "../../helpers"
import { SupportEntityQuery } from "../../supportEntityApi"

type EntityWithHistory = Readonly<{
    initialValues: SupplierFormValues
    history: EntityHistory
}>

function transformData(data: SupplierWithHistory): EntityWithHistory {
    const {
        isActive,
        businessNumber,
        name,
        businessType,
        contactDetails,
        address,
        isAgreementOwner,
        history,
        invoiceDetails,
        subscriptionDetails,
    } = data.entity

    const initialValues = {
        name,
        businessNumber,
        businessType,
        isActive,
        isSystemOwner: AgreementOptions[isAgreementOwner ? "YES" : "NO"],
        email: contactDetails.email,
        contactPerson: contactDetails.name,
        telephone: contactDetails.phone,
        city: address.city,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
        address1: address.addressLines[0],
        address2: address.addressLines[1],
        attention: invoiceDetails?.attention,
        debitorNo: invoiceDetails?.debitorNo,
        eanNumber: invoiceDetails?.endpoint.eanNumber,
        invoiceEmail: invoiceDetails?.endpoint.email,
        invoiceType: invoiceDetails?.endpoint.type,
        poNumber: invoiceDetails?.poNumber,
        reminderEmail: invoiceDetails?.reminderEmail,
        implementationDeadline: subscriptionDetails?.implementationDeadline
            ? DateTime.fromISO(subscriptionDetails?.implementationDeadline)
            : undefined,
    }

    return { initialValues, history }
}

export function UpdateSupplier() {
    const supplierFormRef = useRef<FormikProps<SupplierFormValues>>(null)

    const handleSaveClick = () => {
        supplierFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="support-update-supplier-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_UPDATE_SUPPLIER}
                    title="Update supplier"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="support-create-supplier-update-button">
                            <StandardButton onClick={handleSaveClick}>
                                Update
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-create-supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-supplier-nav-item-entities">
                            <BreadcrumbLink to={SUPPORT_ENTITIES_ROUTE}>Entities</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-supplier-nav-item-update-supplier">
                            <Breadcrumb>Update supplier</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Supplier ref={supplierFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const Supplier = forwardRef<FormikProps<SupplierFormValues>>((_props, ref) => {
    const navigate = useNavigate()
    const { entityId } = useParams()
    const entityIdNumber = Number(entityId)

    const entity = useApiQuery(fetchEntitiesById, {
        queryName: SupportEntityQuery.DETAILS,
        dependencies: {
            entityId: entityIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
        enabled: Boolean(entityId),
    })

    const { mutate } = useApiMutation(updateSupplierApi, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            navigate(SUPPORT_ENTITIES_ROUTE)
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.LIST] })
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.DETAILS] })
        },
    })

    const editSupplier = (value: SupplierFormValues) => {
        if (!entityId) throw new Error("Entity ID is undefined")

        mutate({
            entityId,
            entity: {
                name: value.name,
                isAgreementOwner: value.isSystemOwner === AgreementOptions.YES,
                businessNumber: value.businessNumber,
                businessType: value.businessType as BusinessType,
                address: getAddress(value),
                contactDetails: getContactDetails(value),
                ...getInvoiceDetails(value),
                isActive: value.isActive,
                ...(value.implementationDeadline
                    ? {
                          subscriptionDetails: {
                              implementationDeadline: value.implementationDeadline.toISODate(),
                          },
                      }
                    : {}),
            },
        })
    }

    return (
        <SupplierCreateUpdateForm
            ref={ref}
            history={entity.data?.history}
            initialValues={entity.data?.initialValues}
            onSubmit={editSupplier}
        />
    )
})
